import React, { useState } from 'react';
import { RtModal, RtModalField, RtModalSection } from '../ui/RtModal';
import '../../styles/modal.css';

const ExportOptionsModal = ({ isOpen, onClose, itemType, selectedItems, onExport }) => {
  const [exportFormat, setExportFormat] = useState('excel');
  const [exportOptions, setExportOptions] = useState({
    includeSteps: true,
    includeAttachments: false,
    includeHistory: false,
    includeComments: true
  });
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleExport = () => {
    setLoading(true);

    // Simulate export process
    setTimeout(() => {
      if (onExport) {
        onExport({
          format: exportFormat,
          options: exportOptions,
          items: selectedItems
        });
      }

      setLoading(false);
      onClose();
    }, 1500);
  };

  const handleOptionChange = (option) => {
    setExportOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  const getItemTypeName = () => {
    switch (itemType) {
      case 'testCase':
        return 'Test Case';
      case 'testSuite':
        return 'Test Suite';
      case 'testPlan':
        return 'Test Plan';
      default:
        return 'Item';
    }
  };

  const getItemTypeNamePlural = () => {
    switch (itemType) {
      case 'testCase':
        return 'Test Cases';
      case 'testSuite':
        return 'Test Suites';
      case 'testPlan':
        return 'Test Plans';
      default:
        return 'Items';
    }
  };

  const formats = [
    {
      id: 'excel', label: 'Excel',
      icon: '📊',
      color: 'text-emerald-600', iconBg: 'bg-emerald-50', activeBorder: 'border-emerald-400', activeBg: 'bg-emerald-50', activeText: 'text-emerald-700',
    },
    {
      id: 'pdf', label: 'PDF',
      icon: '📄',
      color: 'text-red-500', iconBg: 'bg-red-50', activeBorder: 'border-red-400', activeBg: 'bg-red-50', activeText: 'text-red-600',
    },
    {
      id: 'json', label: 'JSON',
      icon: '{ }',
      color: 'text-amber-500', iconBg: 'bg-amber-50', activeBorder: 'border-amber-400', activeBg: 'bg-amber-50', activeText: 'text-amber-600',
    },
    {
      id: 'xml', label: 'XML',
      icon: '< />',
      color: 'text-violet-500', iconBg: 'bg-violet-50', activeBorder: 'border-violet-400', activeBg: 'bg-violet-50', activeText: 'text-violet-600',
    },
    {
      id: 'testRail', label: 'TestRail',
      icon: '🧪',
      color: 'text-sky-500', iconBg: 'bg-sky-50', activeBorder: 'border-sky-400', activeBg: 'bg-sky-50', activeText: 'text-sky-600',
    },
    {
      id: 'zephyr', label: 'Zephyr',
      icon: '🔍',
      color: 'text-cyan-500', iconBg: 'bg-cyan-50', activeBorder: 'border-cyan-400', activeBg: 'bg-cyan-50', activeText: 'text-cyan-600',
    },
    {
      id: 'xray', label: 'Xray',
      icon: '📝',
      color: 'text-rose-500', iconBg: 'bg-rose-50', activeBorder: 'border-rose-400', activeBg: 'bg-rose-50', activeText: 'text-rose-600',
    },
  ];

  const optionsList = [
    { key: 'includeSteps', label: 'Include Steps' },
    { key: 'includeAttachments', label: 'Include Attachments' },
    { key: 'includeHistory', label: 'Include History' },
    { key: 'includeComments', label: 'Include Comments' },
  ];

  const integrationConfigs = {
    testRail: {
      title: 'TestRail Integration',
      desc: 'Export directly to your TestRail instance.',
      fields: [
        { label: 'TestRail URL', type: 'text', placeholder: 'https://yourcompany.testrail.io' },
        { label: 'API Key', type: 'password', placeholder: 'Your TestRail API key' },
      ],
    },
    zephyr: {
      title: 'Zephyr Integration',
      desc: 'Export directly to your Zephyr instance.',
      fields: [
        { label: 'Jira URL', type: 'text', placeholder: 'https://yourcompany.atlassian.net' },
        { label: 'API Token', type: 'password', placeholder: 'Your Jira API token' },
      ],
    },
    xray: {
      title: 'Xray Integration',
      desc: 'Export directly to your Xray instance.',
      fields: [
        { label: 'Jira URL', type: 'text', placeholder: 'https://yourcompany.atlassian.net' },
        { label: 'API Token', type: 'password', placeholder: 'Your Xray API token' },
      ],
    },
  };

  const integration = integrationConfigs[exportFormat];

  const CheckIcon = () => (
    <svg viewBox="0 0 12 12" width="9" height="9" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="2 6 5 9 10 3" />
    </svg>
  );

  const subtitle =
    selectedItems?.length > 0
      ? `${selectedItems.length} ${selectedItems.length === 1 ? getItemTypeName().toLowerCase() : getItemTypeNamePlural().toLowerCase()} selected`
      : `All ${getItemTypeNamePlural().toLowerCase()}`;

  const close = () => {
    if (!loading) onClose();
  };

  return (
    <RtModal
      open={isOpen}
      onClose={close}
      title={`Export ${getItemTypeNamePlural()}`}
      subtitle={subtitle}
      size="lg"
      closeDisabled={loading}
      closeOnBackdrop={!loading}
      footer={
        <>
          <button
            type="button"
            className="rt-btn rt-btn--secondary"
            onClick={close}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rt-btn rt-btn--primary"
            onClick={handleExport}
            disabled={loading}
          >
            {loading ? 'Exporting…' : 'Export'}
          </button>
        </>
      }
    >
      <p className="rt-modal__hint" style={{ marginBottom: 16 }}>
        {selectedItems?.length > 0 ? (
          <>
            <strong>{selectedItems.length}</strong>{' '}
            {selectedItems.length === 1
              ? getItemTypeName().toLowerCase()
              : getItemTypeNamePlural().toLowerCase()}{' '}
            will be exported.
          </>
        ) : (
          <>
            All <strong>{getItemTypeNamePlural().toLowerCase()}</strong> in the workspace will be
            exported.
          </>
        )}
      </p>

      <RtModalSection title="Export Format">
        <div className="grid grid-cols-4 gap-2">
          {formats.map((fmt) => {
            const isActive = exportFormat === fmt.id;
            return (
              <button
                key={fmt.id}
                type="button"
                onClick={() => setExportFormat(fmt.id)}
                className={[
                  'flex flex-col items-center gap-2 px-2 py-3 rounded-xl border-[1.5px] transition-all duration-150 cursor-pointer',
                  isActive
                    ? `${fmt.activeBorder} ${fmt.activeBg} shadow-sm -translate-y-px`
                    : 'border-slate-200 bg-slate-50 hover:bg-white hover:border-slate-300 hover:-translate-y-px hover:shadow-sm',
                ].join(' ')}
              >
                <div
                  className={`w-9 h-9 rounded-lg flex items-center justify-center ${fmt.iconBg} ${fmt.color}`}
                >
                  {fmt.icon}
                </div>
                <span
                  className={`text-[11.5px] font-medium ${isActive ? fmt.activeText : 'text-slate-500'}`}
                >
                  {fmt.label}
                </span>
              </button>
            );
          })}
        </div>
      </RtModalSection>

      <RtModalSection title="Export Options">
        <div className="grid grid-cols-2 gap-2">
          {optionsList.map((opt) => {
            const checked = exportOptions[opt.key];
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => handleOptionChange(opt.key)}
                className={[
                  'flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border-[1.5px] text-left transition-all duration-150 cursor-pointer',
                  checked
                    ? 'border-blue-200 bg-blue-50'
                    : 'border-slate-200 bg-slate-50 hover:bg-white hover:border-slate-300',
                ].join(' ')}
              >
                <div
                  className={`w-[17px] h-[17px] rounded-[5px] border-[1.5px] flex items-center justify-center flex-shrink-0 transition-all ${checked ? 'bg-blue-500 border-blue-500' : 'bg-white border-slate-300'}`}
                >
                  {checked && <CheckIcon />}
                </div>
                <span
                  className={`text-[13px] font-medium leading-none ${checked ? 'text-blue-700' : 'text-slate-600'}`}
                >
                  {opt.label}
                </span>
              </button>
            );
          })}
        </div>
      </RtModalSection>

      {integration && (
        <RtModalSection title={integration.title}>
          <p className="rt-modal__hint" style={{ marginBottom: 12 }}>
            {integration.desc}
          </p>
          {integration.fields.map((field, i) => (
            <RtModalField key={i} label={field.label} htmlFor={`export-integration-${i}`}>
              <input
                id={`export-integration-${i}`}
                type={field.type}
                placeholder={field.placeholder}
                disabled={loading}
              />
            </RtModalField>
          ))}
        </RtModalSection>
      )}
    </RtModal>
  );
};

export default ExportOptionsModal;
